// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import styles from '@app/features/user/components/profile/profile_card/ProfileCardBanner.module.css';
import type {User} from '@app/features/user/models/User';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId} from 'react';

interface ProfileCardBannerProps {
	bannerUrl: string | null;
	bannerColor: string;
	user: User;
	avatarUrl: string | null;
	hoverAvatarUrl: string | null;
	disablePresence?: boolean;
	isClickable?: boolean;
	onAvatarClick?: () => void;
	onAvatarContextMenu?: (event: React.MouseEvent) => void;
	onBannerContextMenu?: (event: React.MouseEvent) => void;
	headerHeight?: number;
}
export const ProfileCardBanner: React.FC<ProfileCardBannerProps> = observer(
	({
		bannerUrl,
		bannerColor,
		user,
		avatarUrl,
		hoverAvatarUrl,
		disablePresence = false,
		isClickable = true,
		onAvatarClick,
		onAvatarContextMenu,
		onBannerContextMenu,
		headerHeight = 140,
	}) => {
		const bannerHeight = headerHeight === 140 ? 105 : 105;
		const reactId = useId();
		const safeId = reactId.replace(/[^a-zA-Z0-9_-]/g, '');
		const maskId = `uid_${safeId}`;
		const bannerStyle = {
			height: bannerHeight,
			minHeight: bannerHeight,
			backgroundColor: bannerColor,
			...(bannerUrl ? {backgroundImage: `url(${bannerUrl})`} : {}),
		};
		return (
			<header className={styles.headerSection} style={{height: headerHeight}}>
				<div
					role="group"
					className={styles.bannerWrapper}
					style={{minHeight: bannerHeight}}
					onContextMenu={onBannerContextMenu}
				>
					<svg className={styles.bannerMask} viewBox="0 0 300 105" preserveAspectRatio="none">
						<mask id={maskId}>
							<rect fill="white" x="0" y="0" width="300" height="105" />
							<circle fill="black" cx="54" cy="99" r="44" />
						</mask>
						<foreignObject x="0" y="0" width="300" height="105" overflow="visible" mask={`url(#${maskId})`}>
							<div className={styles.banner} style={bannerStyle} />
						</foreignObject>
					</svg>
				</div>
				<FocusRing offset={-2}>
					<button
						type="button"
						onClick={onAvatarClick}
						onContextMenu={onAvatarContextMenu}
						className={styles.avatarButton}
					>
						<StatusAwareAvatar
							size={80}
							user={user}
							avatarUrl={avatarUrl}
							hoverAvatarUrl={hoverAvatarUrl}
							disablePresence={disablePresence}
							isClickable={isClickable}
						/>
					</button>
				</FocusRing>
			</header>
		);
	},
);
